<template>
    <local-grid
        ref="areasGridRef"
        class="h-full"
        :data-source="state.areasByCurrentAppId"
        :meta="meta"
        :editing="editing"
    >
        <template #toolbarItems>
            <dx-item widget="dxButton" location="after" show-text="inMenu" locate-in-menu="auto">
                <dx-button class="ai-button-base" @click="refreshData" :disabled="changeRefreshDisable">
                    <ai-refresh-base />
                </dx-button>
            </dx-item>

            <dx-item
                v-if="isRoleNonSystem"
                location="after"
            >
                <dx-button
                    class="ai-button-base"
                    hint="Добавить области использования"
                    :disabled="isDisableUiElement"
                    @click="openAreaPopup"
                >
                    <ai-add-base />
                </dx-button>
            </dx-item>

            <dx-item
                v-if="isRoleNonSystem"
                location="after"
            >
                <dx-drop-down-button
                    ref="dropDownButtonRef"
                    :icon="AiPencil"
                    hint="Изменить тип доступа для выделенных областей использования"
                    :items="permissionType"
                    :drop-down-options="{ width: 150 }"
                    :disabled="isDisableUiElement || disabled"
                >
                    <template #itemTemplate="{ data }">
                        <span @click="multipleChange(data)">{{ data.name }}</span>
                    </template>
                </dx-drop-down-button>
            </dx-item>

            <dx-item
                v-if="isRoleNonSystem"
                location="after"
            >
                <dx-button
                    class="ai-button-base"
                    hint="Удалить выделенные"
                    :disabled="isDisableUiElement || disabled"
                    @click="deleteArea"
                >
                    <ai-delete-base />
                </dx-button>
            </dx-item>
        </template>
    </local-grid>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { DxItem } from 'devextreme-vue/data-grid';
import { DxButton, DxDropDownButton } from 'devextreme-vue';
import LocalGrid from '@/components/local-grid.vue';
import { dxUiNotify } from '@/utils/dx-helper.js';
import { showLoadPanel, hideLoadPanel } from '@/components/utils/ai-global-load-panel.js';
import roleLocalStore from '@/views/administration/roles/store.js';
import rolesStore from '@/store/administration/roles.js';
import enviromentStore from '@/store/enviroment-data.js';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog.js';
import AiPencil from '@/assets/ai-pencil.svg';

const { state, MODE, UI_ELEMENTS_IDS, loadById, filterAreasByAppId, isNotSystemRole } = roleLocalStore;
const { rolesState, createRole, updateRole } = rolesStore;
const { disableUiElement } = enviromentStore;

const isRoleNonSystem = isNotSystemRole();
const areasGridRef = ref();
const dropDownButtonRef = ref();
const gridInstance = ref(null);
const disabled = ref(true);
const changeRefreshDisable = computed(() => state.mode === MODE.CREATE ? state.requestBodyHasNotChanged : false);
const isDisableUiElement = disableUiElement(UI_ELEMENTS_IDS.CARD);

const emit = defineEmits(['saveData', 'restartWatching']);

onMounted(() => {
    state.areaGridInstance = areasGridRef.value.gridInstance;
    gridInstance.value = areasGridRef.value.gridInstance;

    gridInstance.value.on('optionChanged', onOptionChanged);
    gridInstance.value.on('selectionChanged', onSelectionChanged);
});

const restartWatching = () => {
    if (isRoleNonSystem) state.stopWatching();
    emit('restartWatching');
};

const openAreaPopup = () => {
    state.rolePopupVisible = false;
    state.areaPopupVisible = true;
};

const onSelectionChanged = event => {
    disabled.value = !event.selectedRowKeys.length;
};

const deleteArea = () => {
    const selectedAreaList = gridInstance.value.getSelectedRowKeys();
    state.areas = state.areas.filter(area => !selectedAreaList.some(item => area.displayId === item));
    filterAreasByAppId();
    gridInstance.value.deselectAll();
};

const saveData = async () => {
    const requestBody = buildRequestBody(state.formData, state.areas);

    if (state.mode === MODE.CREATE) {
        const response = await createRole(requestBody);
        if (response.success) {
            dxUiNotify('Роль создана', 'success');
            state.id = response.role_id;
            state.mode = MODE.UPDATE;
            return { refreshRolesGrid: true };
        } else {
            dxUiNotify(`Ошибка при создании роли: ${response.errors[0].message}`, 'error');
        }
    } else {
        const updatedRequestBody = { ...requestBody, role_id: state.id };
        const { success } = await updateRole(updatedRequestBody);
        if (success) {
            dxUiNotify('Роль обновлена', 'success');
            return { refreshRolesGrid: true };
        } else {
            dxUiNotify('Ошибка при обновлении роли', 'error');
        }
    }
};

const onOptionChanged = event => {
    const { fullName } = event;
    if (fullName !== 'editing.changes') return;
    gridInstance.value.saveEditData();
};

const multipleChange = async data => {
    const newPermission = data.permission;
    const rowsToBeChanged = gridInstance.value.getSelectedRowKeys();

    state.areas.forEach(area => {
        if (rowsToBeChanged.includes(area.displayId)) {
            area.permission = newPermission;
        }
    });

    await gridInstance.value.clearSelection();
    await dropDownButtonRef.value.instance.close();
};

const showSaveDataConfirmDialog = async () => {
    const result = await AiCustomConfirmDialog('Сохранить изменения?', 'Запрос на сохранение');
    if (result === -1) return true;
    else if (result === 1) emit('saveData');
    return false;
};

const refreshData = async () => {
    try {
        showLoadPanel();
        if (!state.requestBodyHasNotChanged) {
            const isCancel = await showSaveDataConfirmDialog();
            if (isCancel) return;
        }

        if (state.mode !== MODE.CREATE) {
            await loadById(state.id, state.appIds);
        }
        restartWatching();
        dxUiNotify('Данные обновлены', 'success');
    } finally {
        hideLoadPanel();
    }
};

const buildRequestBody = (formData, areas) => {
    const { code, name, description } = formData;

    const result = {
        code,
        name,
        description,
        areas: [],
    };

    areas.forEach(area => {
        const preparedArea = {
            area_id: area.id,
            permission: area.permission,
            kod_app: area.app_id,
        };
        result.areas.push(preparedArea);
    });

    return result;
};

const permissionType = [
    { permission: 'R', name: 'Чтение' },
    { permission: 'W', name: 'Запись' },
];

const editing = {
    mode: 'batch',
    allowUpdating: true,
};

const isWritePermissionTypeColumn = computed(() => !isDisableUiElement && isRoleNonSystem);

const meta = {
    title: 'Список областей использования',
    keyExpr: 'displayId',
    scrollingMode: 'standart',
    allowSelectAll: true,
    selectionMode: 'multiple',
    columns: [
        {
            dataField: 'group_id',
            caption: 'Группа',
            groupIndex: 0,
            lookup: {
                dataSource: rolesState.groupList,
                valueExpr: 'id',
                displayExpr: 'name',
            },
        },
        {
            dataField: 'id',
            caption: 'id',
            allowEditing: false,
            visible: false,
        },
        {
            dataField: 'name',
            caption: 'Область использования',
            allowEditing: false,
        },
        {
            dataField: 'permission',
            caption: 'Тип доступа',
            allowEditing: isWritePermissionTypeColumn.value,
            lookup: {
                dataSource: permissionType,
                valueExpr: 'permission',
                displayExpr: 'name',
            },
        },
        {
            dataField: 'description',
            caption: 'Описание',
            allowEditing: false,
        },
    ],
};

defineExpose({
    saveData,
});
</script>
